import React from 'react';
import { useSpring, animated } from 'react-spring';
import { Formik, Form, Field } from 'formik';
import styled from '@emotion/styled';
import * as Yup from 'yup';
import { useTheme } from 'emotion-theming';
import { Global, css } from '@emotion/core';
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../../../shared/components/Button/Button';
import Checkmark from '../../../../shared/components/Checkmark/Checkmark';
import Text from '../../../../shared/components/Text/Text';
import Link from '../../../../shared/components/Link/Link';
import encode from '../../../../shared/utils/encodeToUrl';
import useLogin from '../../../login/components/hooks/useLogin';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import { registerUser } from '../../../registration/ducks/registrationActions';

const FormGroup = styled('div')`
  p {
    margin-bottom: 8px;
  }
  label {
    width: 100%;
  }
  button {
    margin-bottom: 20px;
  }
  .easy-join {
    margin-top: 30px;
    margin-bottom: 15px;
  }
  .checkbox {
    margin-top: 2px;
    margin-right: 10px;
  }

  .spinner-container {
    min-height: 60px;
  }
  
  z-index: 1;

  &.d-flex {
    margin-bottom: 10px;
  }
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
`;

const FieldError = styled('div')`
  margin-top: 10px;
  font-size: 14px;
  color: #ff6600;
`;

export const SpinnerContainer = styled('div')`
  display: flex;
  height: 100%;
  flex: 1;
  position: absolute;
  top: 0;
  justify-content: center;
  width: 100%;
  margin-left: -54px;
  z-index: 0;

  .checkmark {
    display: flex;
    align-self: center;
  }
`;

const FormColumns = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
  label {
    margin-bottom: 0px;
  }
`;

const ColumnContent = styled('div')`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  :first-of-type {
    margin-right: 30px;
  }
`;

const LoginContainer = styled('div')`
  position: relative;
`;

const LoginSchema = Yup.object().shape({
  emailAddress: Yup.string()
    .email('Invalid Email Address')
    .required('Email Required'),
  password: Yup.string()
    .required('Password Required'),
  confirmedPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  agreementTerms: Yup.mixed().oneOf([true], 'Field must be checked'),
});

const ModalCreateAccount = ({
  resellerSlug,
}) => {
  const {
    isLoginLoading,
    isAuthenticated,
  } = useLogin();

  const registrationError = useSelector((state) => state.auth.data.registrationError);
  const isRegistrationLoading = useSelector((state) => state.auth.ui.isRegistrationLoading);

  const formAnimation = useSpring({
    opacity: isLoginLoading || isAuthenticated ? 0 : 1,
    zIndex: 1,
    position: 'relative',
  });

  const spinnerAnimation = useSpring({ opacity: isLoginLoading ? 1 : 0, zIndex: 0 });
  const completeAnimation = useSpring({ opacity: isAuthenticated ? 1 : 0 });

  const dispatch = useDispatch();
  const theme = useTheme();

  return (
    <Formik
      initialValues={{
        firstName: '',
        lastName: '',
        emailAddress: '',
        password: '',
        confirmedPassword: '',
        mobileNumber: '',
        authenticationMethod: 'PASSWORD',
        registrationType: 'EXISTING_ORGANIZATION',
        resellerSlug,
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        scope: 'offline_access',
        agreementTerms: false,
      }}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          dispatch(registerUser(encode(values)));
          setSubmitting(false);
        });
      }}
      validationSchema={LoginSchema}
      render={({
        errors,
        touched,
        handleSubmit,
        values,
        setFieldValue,
      }) => (
        <LoginContainer>
          <Global
            styles={css`
              .ReactModal__Content {
                width: 420px!important;
              }
            `}
          />
          <>
            <animated.div style={formAnimation}>
              <Form
                name='login'
                id='login'
              >
                <FormGroup className='form-group'>
                  <FormColumns>
                    <ColumnContent>
                      <label>
                        <Text
                          fontSize={theme.textFontSizes[0]}
                        >
                          First
                        </Text>
                        <Field
                          className='form-control'
                          id='firstName'
                          type='text'
                          name='firstName'
                          placeholder='First Name'
                        />
                      </label>
                    </ColumnContent>
                    <ColumnContent>
                      <label>
                        <Text
                          fontSize={theme.textFontSizes[0]}
                        >
                          Last
                        </Text>
                        <Field
                          className='form-control'
                          id='lastName'
                          type='text'
                          name='lastName'
                          placeholder='Last Name'
                        />
                      </label>
                    </ColumnContent>
                  </FormColumns>
                </FormGroup>
                <FormGroup className='form-group'>
                  <label>
                    <Text
                      fontSize={theme.textFontSizes[0]}
                    >
                      Email Address
                    </Text>
                    <Field
                      className='form-control'
                      id='email-field'
                      type='email'
                      name='emailAddress'
                      placeholder='Email'
                    />
                  </label>
                  {errors.emailAddress && touched.emailAddress && <FieldError className='text-center'>{errors.emailAddress}</FieldError>}
                </FormGroup>
                <FormGroup className='form-group'>
                  <label>
                    <Text
                      fontSize={theme.textFontSizes[0]}
                    >
                      New Password
                    </Text>
                    <Field
                      autoComplete='off'
                      className='form-control'
                      type='password'
                      name='password'
                      placeholder='Password'
                    />
                  </label>
                  {errors.password && touched.password && <FieldError className='text-center'>{errors.password}</FieldError>}
                </FormGroup>
                <FormGroup className='form-group'>
                  <label>
                    <Text
                      fontSize={theme.textFontSizes[0]}
                    >
                      Re-enter Password
                    </Text>
                    <Field
                      autoComplete='off'
                      className='form-control'
                      type='password'
                      name='confirmedPassword'
                      placeholder='Password'
                    />
                  </label>
                  {errors.confirmedPassword && touched.confirmedPassword && <FieldError className='text-center'>{errors.confirmedPassword}</FieldError>}
                </FormGroup>
                <FormGroup className='form-group'>
                  <label>
                    <Text
                      fontSize={theme.textFontSizes[0]}
                    >
                      Phone
                    </Text>
                    <Field
                      className='form-control'
                      id='email-field'
                      type='phone'
                      name='mobileNumber'
                      placeholder='Phone Number'
                    />
                  </label>
                  {registrationError && <FieldError className='text-center'>{registrationError.message}</FieldError>}
                </FormGroup>
                <FormGroup className='d-flex'>
                  <label className='d-flex'>
                    <input
                      className='checkbox'
                      type='checkbox'
                      name='agreementTerms'
                      onChange={() => {
                        setFieldValue('agreementTerms', !values.agreementTerms && true);
                        Logger.debug(values);
                      }}
                      checked={values.agreementTerms}
                    />
                    <Text fontSize={theme.textFontSizes[0]}>
                      I acknowledge and agree to the
                      {' '}
                      <Link href='https://www.teambuildr.com/user-agreement' target='_blank'>TeamBuildr User Agreement</Link>
                    </Text>
                  </label>
                </FormGroup>
                <FormGroup className='form-group'>
                  {errors.agreementTerms && touched.agreementTerms && <FieldError className='text-center'>Must Accept Terms Before Creating Account.</FieldError>}
                </FormGroup>
                <FormGroup className='text-center'>
                  {isRegistrationLoading ? (
                    <Spinner saving darkTheme />
                  ) : (
                    <Button
                      cta='Submit'
                      customColor='#ff6600'
                      disabled={
                        errors.password
                        || errors.emailAddress
                        || errors.confirmedPassword
                        || errors.agreementTerms
                      }
                      name='submit'
                      id='submit'
                      onClick={handleSubmit}
                      fullWidth
                      large
                      noBorder
                      rounded
                      primary
                    />
                  )}
                </FormGroup>
              </Form>
            </animated.div>
            <animated.div style={spinnerAnimation}>
              <SpinnerContainer>
                <Spinner />
              </SpinnerContainer>
            </animated.div>
            <animated.div style={completeAnimation}>
              <SpinnerContainer>
                <Checkmark />
              </SpinnerContainer>
            </animated.div>
          </>
        </LoginContainer>
      )}
    />
  );
};

export default ModalCreateAccount;
